import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  TextField,
  useTheme,
  InputAdornment,
  IconButton,
  Link,
  Stack,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useAuth } from 'auth'
import logger from 'logger'
import config from 'config'

import InputLabel from 'components/atoms/InputLabel'

const SignIn = ({
  setView,
  state,
  setError,
  error,
  setState,
  loginError,
  setFetching,
  fetching,
  setLoginError,
}) => {
  const theme = useTheme()
  const { updateAuth, AmplifyAuth, setAuthEvent } = useAuth()

  const { t } = useTranslation('auth')

  useEffect(() => {
    setState((s) => ({ ...s, email: '', password: '' }))
  }, [setState])

  const handleInputChange = (event) => {
    setLoginError()
    setState((s) => ({ ...s, [event.target.name]: event.target.value }))
  }

  const handleSignIn = async (event) => {
    event.preventDefault()
    event.stopPropagation()

    try {
      setFetching(true)

      const result = await AmplifyAuth.signIn({
        username: state.email,
        password: state.password,
      })

      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setAuthEvent(result)
        setView('forceChangePassword')
        return
      }

      if (
        result.preferredMFA === 'NOMFA' ||
        result.challengeName === 'MFA_SETUP'
      ) {
        setAuthEvent(result)
        setView('mfaSetup')
        return
      }

      if (result.challengeName === 'SOFTWARE_TOKEN_MFA') {
        setAuthEvent(result)
        setView('mfaVerify')
        return
      }

      const claims = JSON.parse(
        result?.signInUserSession?.idToken?.payload[config.auth.claims] || '{}'
      )

      if (!claims.enabled) {
        setView('accountDisabled')
        return
      }

      await updateAuth()
      setView('loggedIn')
    } catch (e) {
      if (e.code === 'NotAuthorizedException') {
        setError(e.code)
        return
      }
      if (e.code === 'UserNotFoundException') {
        setError(e.code)
        return
      }

      logger.error(e)
      setError(e.code)
    } finally {
      setFetching(false)
    }
  }
  const [showPassword, setShowPassword] = useState(false)

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Stack
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        width: 0.5,
        maxWidth: 400,
      }}
    >
      <form onSubmit={handleSignIn} style={{ width: '100%' }}>
        <Typography variant="h4" sx={{ pb: 1 }} data-testid="signin-title">
          <strong>{t('headers.signIn.title')}</strong>
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ pb: 5 }}>
          <strong>{t('headers.signIn.subtitle')}</strong>
        </Typography>
        <Box sx={{ width: 1 }}>
          <InputLabel
            label={t('attributes.email.label')}
            inputId="login-email"
            sx={{ textAlign: 'left' }}
          />
          <TextField
            id="login-email"
            size="small"
            fullWidth
            placeholder={t('attributes.email.placeholder')}
            name="email"
            value={state.email}
            onChange={handleInputChange}
            disabled={fetching}
            InputProps={{
              autoFocus: true,
              'data-testid': 'signIn-email',
            }}
          />
        </Box>

        <Box mb={3} />

        <Box sx={{ width: 1 }}>
          <InputLabel
            label={t('attributes.password.label')}
            inputId="login-password"
            sx={{ textAlign: 'left' }}
          />
          <TextField
            id="login-password"
            size="small"
            fullWidth
            placeholder={t('attributes.password.placeholder')}
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={state.password}
            onChange={handleInputChange}
            disabled={fetching}
            InputProps={{
              'data-testid': 'signIn-password',
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleShowPassword}
                    edge="end"
                    data-testid="signIn-toggleViewPassword"
                  >
                    {showPassword ? (
                      <VisibilityOff data-testid="signIn-passwordVisibleOff" />
                    ) : (
                      <Visibility data-testid="signIn-passwordVisibleOn" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box mb={3} />
        {loginError && (
          <Typography
            textAlign="center"
            p={[3]}
            color="red"
            data-testid="signIn-loginError"
          >
            {t(`errors.${loginError}`, state)}
          </Typography>
        )}

        <LoadingButton
          fullWidth
          width="240px"
          variant="contained"
          color="primary"
          loading={fetching}
          disabled={fetching}
          data-testid="signIn-button"
          type="submit"
        >
          {t('buttons.signIn')}
        </LoadingButton>
        {error && (
          <Typography
            textAlign="center"
            p={[3]}
            style={{ color: theme.palette.error.light }}
            data-testid="signIn-error"
          >
            {t(`errors.${error}`, state)}
          </Typography>
        )}

        <Box mb={3} />
        <Link>
          <Typography
            data-testid="signIn-passwordForgot"
            variant="body2"
            color="primary"
            onClick={() => {
              setState((s) => ({ ...s, password: '' }))
              setView('passwordForgot')
            }}
            sx={{ cursor: 'pointer' }}
          >
            <strong>{t('buttons.signInPasswordForgot')}</strong>
          </Typography>
        </Link>
      </form>
    </Stack>
  )
}

export default SignIn
